import {
  Children,
  isValidElement,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
  type SelectHTMLAttributes,
} from "react";

import { FormField } from "./FormField";
import "./Dropdown.scss";

type DropdownProps = SelectHTMLAttributes<HTMLSelectElement> & {
  children?: ReactNode;
  error?: string;
  hint?: string;
  label?: string;
  placeholder?: string;
  withSearch?: boolean;
  clearable?: boolean;
};

type DropdownOption = {
  label: ReactNode;
  searchLabel: string;
  value: string;
};

function optionLabel(label: ReactNode) {
  return typeof label === "string" || typeof label === "number"
    ? String(label)
    : "";
}

function selectedValue(
  value: string | number | readonly string[] | undefined,
  defaultValue: string | number | readonly string[] | undefined,
  options: DropdownOption[],
) {
  const candidate = value ?? defaultValue;
  if (Array.isArray(candidate)) return candidate[0] ?? "";
  return candidate !== undefined ? String(candidate) : options[0]?.value ?? "";
}

export function Dropdown({
  children,
  clearable = false,
  className = "",
  defaultValue,
  disabled = false,
  error,
  hint,
  label,
  multiple,
  onChange,
  placeholder,
  value,
  withSearch = false,
  ...props
}: DropdownProps) {
  const options = useMemo(
    () =>
      Children.toArray(children).flatMap((child) => {
        if (!isValidElement<{ children?: ReactNode; value?: string }>(child)) {
          return [];
        }
        const optionValue = child.props.value;
        if (optionValue === undefined) return [];
        const searchLabel = optionLabel(child.props.children);
        return [{ label: child.props.children, searchLabel, value: optionValue }];
      }),
    [children],
  );
  const nativeSelectRef = useRef<HTMLSelectElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [currentValue, setCurrentValue] = useState(() =>
    selectedValue(value, defaultValue, options),
  );
  const isSearchable = withSearch;
  const activeValues = Array.isArray(value)
    ? value.map(String)
    : String(value ?? defaultValue ?? "")
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
  const activeValue =
    value === undefined
      ? currentValue
      : selectedValue(value, undefined, options);
  const activeOption = options.find((option) => option.value === activeValue);
  const displayValue = multiple
    ? activeValues
        .map((selected) => optionLabel(options.find((option) => option.value === selected)?.label))
        .filter(Boolean)
        .join(", ")
    : optionLabel(activeOption?.label);
  const filteredOptions = options.filter((option) =>
    option.searchLabel.toLowerCase().includes(query.trim().toLowerCase()),
  );

  const clear = () => {
    const nativeSelect = nativeSelectRef.current;
    if (!nativeSelect) return;
    Array.from(nativeSelect.options).forEach((option) => {
      option.selected = false;
    });
    nativeSelect.dispatchEvent(new Event("change", { bubbles: true }));
    setCurrentValue("");
    setQuery("");
    setOpen(false);
  };

  const choose = (nextValue: string) => {
    setCurrentValue(nextValue);
    setQuery("");
    setOpen(false);

    const nativeSelect = nativeSelectRef.current;
    if (!nativeSelect) return;
    const valueSetter = Object.getOwnPropertyDescriptor(
      HTMLSelectElement.prototype,
      "value",
    )?.set;
    valueSetter?.call(nativeSelect, nextValue);
    nativeSelect.dispatchEvent(new Event("change", { bubbles: true }));
  };

  const toggle = (nextValue: string) => {
    const nextValues = activeValues.includes(nextValue)
      ? activeValues.filter((item) => item !== nextValue)
      : [...activeValues, nextValue];
    const nativeSelect = nativeSelectRef.current;
    if (!nativeSelect) return;
    Array.from(nativeSelect.options).forEach((option) => {
      option.selected = nextValues.includes(option.value);
    });
    nativeSelect.dispatchEvent(new Event("change", { bubbles: true }));
    setQuery("");
    setOpen(true);
  };

  const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown" || event.key === "Enter") {
      event.preventDefault();
      setOpen(true);
      if (event.key === "Enter" && filteredOptions[0]) {
        choose(filteredOptions[0].value);
      }
    }
    if (event.key === "Escape") setOpen(false);
  };

  if (!isSearchable) {
    return (
      <FormField error={error} hint={hint} label={label}>
        <select
          className={`dropdown ${className}`.trim()}
          disabled={disabled}
          multiple={multiple}
          onChange={onChange}
          value={value}
          defaultValue={defaultValue}
          {...props}
        >
          {children}
        </select>
      </FormField>
    );
  }

  return (
    <FormField error={error} hint={hint} label={label}>
      <div
        className={`dropdown-searchable ${open ? "dropdown-searchable--open" : ""}`}
      >
        <input
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-label={props["aria-label"] ?? label}
          className={`dropdown-search ${className}`.trim()}
          disabled={disabled}
          id={props.id}
          onBlur={() => window.setTimeout(() => setOpen(false), 0)}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onFocus={() => {
            setQuery("");
            setOpen(true);
          }}
          onKeyDown={handleSearchKeyDown}
          placeholder={placeholder ?? displayValue}
          role="combobox"
          type="search"
          value={open ? query : displayValue}
        />
        {clearable && multiple && activeValues.length > 0 && (
          <button
            aria-label={`Clear ${label ?? "selection"}`}
            className="dropdown-clear"
            onClick={clear}
            type="button"
          >
            ×
          </button>
        )}
        {open && (
          <div className="dropdown-options" role="listbox">
            {filteredOptions.length ? (
              filteredOptions.map((option) => (
                <button
                  aria-selected={multiple ? activeValues.includes(option.value) : option.value === activeValue}
                  className={
                    multiple
                      ? activeValues.includes(option.value)
                        ? "is-selected"
                        : ""
                      : option.value === activeValue
                        ? "is-selected"
                        : ""
                  }
                  key={option.value}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => (multiple ? toggle(option.value) : choose(option.value))}
                  role="option"
                  type="button"
                >
                  {option.label}
                </button>
              ))
            ) : (
              <span className="dropdown-empty">No matches</span>
            )}
          </div>
        )}
        <select
          aria-hidden="true"
          className="dropdown-native"
          name={props.name}
          multiple={multiple}
          onChange={onChange}
          ref={nativeSelectRef}
          tabIndex={-1}
          value={multiple ? activeValues : activeValue}
        >
          {children}
        </select>
      </div>
    </FormField>
  );
}
